import { FC, useCallback, useContext } from "react";
import { useSSTranslation } from "@/i18n/useSSTranslation";
import { SocketContext } from "@/contexts/socketContext";
import { ENROLLMENT_TYPES, JobStatusCallback, SOCKET_EVENTS, StartPlaylistEnrollmentsPayload } from "@buildwitt/shapeshift-types";
import ConfirmationDialog from "@/components/common/dialogs/ConfirmationDialog";
import { Typography } from "@mui/material";

interface IProps {
	open: boolean;
	onClose: (reload?: boolean) => void;
	selectedUserIds: number[];
	playlistId: number;
	onLoadingChange: (isLoading: boolean) => void;
}

const AdminBulkPlaylistEnrollmentConfirmationModal: FC<IProps> = ({open, onClose, selectedUserIds, playlistId, onLoadingChange}) => {
	const {t} = useSSTranslation();
	const socket = useContext(SocketContext);

	const handleConfirm = useCallback(() => {
		if (!open || !socket) {
			return;
		}

		const payload: StartPlaylistEnrollmentsPayload = {
			userIds: selectedUserIds,
			playlistId,
			enrollmentType: ENROLLMENT_TYPES.PRIORITIZE_SCHEDULE
		};

		onLoadingChange(true);

		socket.emit(SOCKET_EVENTS.START_PLAYLIST_ENROLLMENTS, payload, (response: JobStatusCallback) => {
			window.dispatchEvent(new CustomEvent("playlistEnrollmentsCompleted", {detail: response}));
		});

		onClose();
	}, [open, socket, selectedUserIds, playlistId, onLoadingChange, onClose]);

	return (
		<ConfirmationDialog 
			title={t("bulk_enrollment_confirm_title", "Confirm Bulk Enrollment")}
			testIdPrefix="bulkPlaylistEnrollment"
			open={open}
			hideCloseIcon
			onCancel={() => onClose()}
			onConfirm={handleConfirm}
		>
			<Typography paddingTop={1}>
				{t("bulk_playlist_enrollment_modal_content_1", "Enrolling a user onto a playlist will add training to the top of your employee's training feed.")}<br />
				<br />
				{t("bulk_playlist_enrollment_modal_content_2", "Important: Please note that prioritizing courses may push other items down the list and impact other courses due dates.")}
			</Typography>
			<Typography sx={{ paddingLeft: "20px" }}>
				<br />
				<li>{t("bulk_playlist_enrollment_modal_content_3", "{{users}} users selected", {users: selectedUserIds.length})}</li>
			</Typography>
		</ConfirmationDialog>
	);
}

export default AdminBulkPlaylistEnrollmentConfirmationModal;